import type { CdnV1, CdnV2, MapiV1 } from '@storyblok/openapi'
import axios from 'axios'
import createClient from 'openapi-fetch'
import pThrottle from 'p-throttle'
import { stringify } from 'qs'
import config from '../config.js'
import {
  successInterceptor,
  errorInterceptor,
  beforeRequestInterceptor,
  requestErrorInterceptor,
  withAuthorization,
  getAccessToken,
} from './interceptors'

export const defaultHeaders = {
  'SB-Agent': 'SB-FE',
  'SB-Agent-Version': process.env.VUE_APP_VERSION || '2.0.0',
}

export const getCv = () => new Date().setHours(12)

/*
 * Axios client for making authenticated request to the management API.
 * @deprecated do not use this in new code. Use `managementApiClient` instead.
 * @see managementApiClient
 */
export const client = axios.create({
  baseURL: config.get('api'),
  paramsSerializer: (params) => stringify(params, { arrayFormat: 'brackets' }),
  headers: {
    ...defaultHeaders,
    common: {
      Authorization: getAccessToken(),
    },
  },
})

/**
 * Not sure what this client is used for /Johannes
 * @deprecated use `managementApiClient` instead
 */
export const uninterceptedClient = axios.create({
  baseURL: config.get('api'),
  paramsSerializer: (params) => stringify(params, { arrayFormat: 'brackets' }),
})

uninterceptedClient.interceptors.request.use(beforeRequestInterceptor)

client.interceptors.response.use(successInterceptor, errorInterceptor)

client.interceptors.request.use(
  beforeRequestInterceptor,
  requestErrorInterceptor,
)

/* openapi client */
const createDeliveryApiClient = () => {
  const throttle = pThrottle({
    // 3 requests per second is a pessimistic assumption
    limit: 3,
    interval: 1000,
  })

  return createClient<CdnV1.paths & CdnV2.paths>({
    baseUrl: 'https://api.storyblok.com',
    querySerializer: (params) => stringify(params, { arrayFormat: 'brackets' }),
    fetch: throttle(fetch),
  })
}

const throttle = pThrottle({
  // The actual rate limit is 500 requests per second
  limit: 250,
  interval: 1000,
})

// Instantiate a shared throttle at the module level so that the throttling state is shared across the app
const throttledAndAuthorizedFetch = throttle(withAuthorization(fetch))

/**
 * Call the management API (MAPI).
 * @example
 * With region
 * ```ts
 * // TODO
 * ```
 * @example
 * Without region
 * ```ts
 * // TODO
 * ```
 * @param baseUrl
 */
export const managementApiClient = (baseUrl: string) =>
  createClient<MapiV1.paths>({
    baseUrl,
    querySerializer: (params) => stringify(params, { arrayFormat: 'brackets' }),
    fetch: throttledAndAuthorizedFetch,
  })

// We benefit from two instances because they are used to query different spaces, and thus have different throttling
export const partnerSpaceClient = createDeliveryApiClient()
// export const appSpaceClient = createDeliveryApiClient()
